import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AdminLayout from '../../components/AdminLayout';

// ========== 样式组件 ==========
const Container = styled.div` max-width: 1320px; margin: 0 auto; padding: 0 1rem; `;
const SectionTitle = styled.h1` font-size: 2rem; font-weight: 700; margin-bottom: 1.5rem; color: var(--gray-900); `;
const Form = styled.form` background: var(--white); padding: 2rem; border-radius: var(--border-radius-lg); box-shadow: var(--box-shadow-sm); `;
const FormRow = styled.div` display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 1.5rem; `;
const FormGroup = styled.div` margin-bottom: 1.5rem; `;
const Label = styled.label` display: block; margin-bottom: 0.5rem; font-weight: 500; color: var(--gray-700); `;
const Input = styled.input`
  width: 100%; padding: 0.75rem; border: 1px solid var(--gray-300); border-radius: var(--border-radius-md); font-size: 1rem;
  &:focus { outline: none; border-color: var(--primary-color); box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
`;
const Textarea = styled.textarea`
  width: 100%; padding: 0.75rem; border: 1px solid var(--gray-300); border-radius: var(--border-radius-md); font-size: 1rem; resize: vertical;
  min-height: ${props => props.$minH || '100px'};
  &:focus { outline: none; border-color: var(--primary-color); box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
`;
const Select = styled.select`
  width: 100%; padding: 0.75rem; border: 1px solid var(--gray-300); border-radius: var(--border-radius-md); font-size: 1rem; background: white;
  &:focus { outline: none; border-color: var(--primary-color); box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
`;
const TagContainer = styled.div` display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.5rem; `;
const Tag = styled.span`
  display: inline-flex; align-items: center; padding: 0.25rem 0.75rem;
  background-color: ${props => props.$bg || 'var(--primary-light)'}; color: ${props => props.$color || 'white'};
  border-radius: var(--border-radius-full); font-size: 0.875rem;
`;
const TagInput = styled.input`
  padding: 0.5rem; border: 1px solid var(--gray-300); border-radius: var(--border-radius-md); font-size: 0.875rem; width: 180px;
  &:focus { outline: none; border-color: var(--primary-color); }
`;
const Button = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 0.4rem;
  padding: 0.75rem 1.5rem; background-color: var(--primary-color); color: white; border: none;
  border-radius: var(--border-radius-md); font-weight: 500; cursor: pointer;
  &:hover { opacity: 0.9; }
  &:disabled { background-color: var(--gray-400); cursor: not-allowed; }
`;
const CancelButton = styled(Button)` background-color: var(--gray-200); color: var(--gray-700); margin-right: 1rem; &:hover { background-color: var(--gray-300); } `;
const QuillWrapper = styled.div`
  .ql-toolbar { border-radius: 6px 6px 0 0; border-color: var(--gray-300); background: #fafafa; }
  .ql-container { border-radius: 0 0 6px 6px; border-color: var(--gray-300); min-height: 200px; font-size: 1rem; }
  .ql-editor { min-height: 200px; }
`;
const SmallButton = styled.button`
  padding: 0.4rem 0.75rem; border: 1px solid var(--gray-300); border-radius: var(--border-radius-sm);
  background: var(--white); font-size: 0.8rem; cursor: pointer; &:hover { background: var(--gray-100); }
`;
const ErrorMessage = styled.div` color: var(--danger-color); font-size: 0.875rem; margin-bottom: 0.5rem; `;
const SuccessMessage = styled.div`
  color: var(--success-color); font-size: 0.875rem; padding: 0.75rem;
  background-color: var(--success-light); border-radius: var(--border-radius-md); margin-bottom: 1rem;
`;
const SubTitle = styled.h3`
  font-size: 1.1rem; font-weight: 600; color: var(--gray-800); margin: 2rem 0 1rem;
  padding-bottom: 0.5rem; border-bottom: 2px solid var(--primary-light);
`;
const SectionBlock = styled.div`
  margin-top: 1rem; padding: 1.25rem; background: ${props => props.$bg || '#f8f9fa'};
  border-radius: 8px; border: 1px solid #e9ecef;
`;

// 图片管理
const ImageGrid = styled.div` display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 0.75rem; margin-top: 0.5rem; `;
const ImageCard = styled.div` position: relative; aspect-ratio: 1; border-radius: 8px; overflow: hidden; border: 2px solid var(--gray-200); `;
const ImagePreview = styled.img` width: 100%; height: 100%; object-fit: cover; `;
const ImageDeleteBtn = styled.button`
  position: absolute; top: 4px; right: 4px; width: 24px; height: 24px; border-radius: 50%;
  background: rgba(220, 53, 69, 0.85); color: white; border: none; cursor: pointer; font-size: 14px;
  display: flex; align-items: center; justify-content: center; &:hover { background: #dc3545; }
`;
const UploadArea = styled.label`
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.5rem;
  padding: 2rem; border: 2px dashed var(--gray-300); border-radius: 8px; cursor: pointer;
  color: var(--gray-500); transition: all 0.2s;
  &:hover { border-color: var(--primary-color); color: var(--primary-color); }
  input { display: none; }
`;

// AI
const AISection = styled.div` background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: var(--border-radius-lg); padding: 1.25rem; margin-bottom: 1.5rem; color: white; `;
const AISectionHeader = styled.div` display: flex; justify-content: space-between; align-items: center; font-size: 1rem; font-weight: 600; `;
const AIToggleButton = styled.button` background: rgba(255,255,255,0.2); color: white; border: 1px solid rgba(255,255,255,0.3); padding: 0.25rem 1rem; border-radius: var(--border-radius-full); cursor: pointer; font-size: 0.8rem; &:hover { background: rgba(255,255,255,0.3); } `;
const AIBody = styled.div` margin-top: 1rem; padding-top: 1rem; border-top: 1px solid rgba(255,255,255,0.2); `;
const AITextarea = styled.textarea`
  width: 100%; padding: 0.75rem; border: 1px solid rgba(255,255,255,0.3); border-radius: var(--border-radius-md);
  font-size: 0.875rem; background: rgba(255,255,255,0.1); color: white; resize: vertical; min-height: 100px;
  &::placeholder { color: rgba(255,255,255,0.6); } &:focus { outline: none; border-color: rgba(255,255,255,0.6); }
`;
const AIButtonRow = styled.div` display: flex; align-items: center; margin-top: 0.75rem; `;
const AIConvertButton = styled.button`
  padding: 0.6rem 1.5rem; background: white; color: #667eea; border: none; border-radius: var(--border-radius-md);
  font-weight: 600; cursor: pointer; &:hover { background: #f0f0ff; }
  &:disabled { background: rgba(255,255,255,0.3); color: rgba(255,255,255,0.8); cursor: not-allowed; }
`;

// ========== 常量 ==========
const SOLUTION_TYPES = ['通用', '行业', '技术'];
const INDUSTRIES = {
  '通用': ['通信相关', '技术咨询', '基础设施', 'Ai相关', '云服务', '网络安全', '数据中心', '企业数字化'],
  '行业': ['医疗卫生', '生产制造', '农林牧渔', '建筑地产', '金融行业', '教育行业', '智慧城市', '智慧交通', '能源电力', '应急指挥', '公共安全', '园区景区', '酒店公寓', '餐饮零售', '物流仓储', '媒体文娱', '电子商务', '教育培训', '政府机构', '交通运输', '水利环保', '文化旅游', '会展活动', '体育健身'],
  '技术': ['大数据', '人工智能', '云计算', '物联网', '区块链', '5G应用', '边缘计算', '虚拟现实', '工业互联网']
};
const STATUS_OPTIONS = [
  { value: 'active', label: '已发布（前台可见）' },
  { value: 'draft', label: '草稿（不公开）' },
  { value: 'inactive', label: '已下架（不公开）' }
];

const emptySolution = () => ({
  title: '', description: '', type: '行业', industry: '医疗卫生',
  category: '', icon: '', content: '', images: [],
  features: [], benefits: [], applicationScenarios: [], technicalHighlights: [],
  subSolutions: [], tags: [], keywords: [], skills: [], constraints: [], status: 'active'
});

// ========== 组件 ==========
const AdminSolutionForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const fileInputRef = useRef(null);

  console.log('AdminSolutionForm 渲染, id:', id, 'isEditMode:', isEditMode);

  const [solution, setSolution] = useState(emptySolution());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // 动态输入
  const [newTag, setNewTag] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [newFeature, setNewFeature] = useState('');
  const [newScenario, setNewScenario] = useState('');
  const [newBenefit, setNewBenefit] = useState('');
  const [newTech, setNewTech] = useState('');
  const [newConstraint, setNewConstraint] = useState('');
  const [newSkillName, setNewSkillName] = useState('');
  const [newSkillDesc, setNewSkillDesc] = useState('');
  const [newSubIcon, setNewSubIcon] = useState('');
  const [newSubTitle, setNewSubTitle] = useState('');
  const [newSubDesc, setNewSubDesc] = useState('');

  // 图片
  const [uploadingImages, setUploadingImages] = useState(false);

  // AI
  const [showAI, setShowAI] = useState(true);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMessage, setAiMessage] = useState('');

  // ========== 加载编辑数据 ==========
  useEffect(() => {
    if (id) fetchSolution();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchSolution = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`/api/admin/solutions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.solution) {
        const s = response.data.solution;
        setSolution({
          title: s.title || '',
          description: s.description || '',
          type: s.type || '行业',
          industry: s.industry || '医疗卫生',
          category: s.category || '',
          icon: s.icon || '',
          content: s.content || '',
          images: s.images || [],
          features: s.features || [],
          benefits: s.benefits || [],
          applicationScenarios: s.applicationScenarios || [],
          technicalHighlights: s.technicalHighlights || [],
          subSolutions: s.subSolutions || [],
          tags: s.tags || [],
          keywords: s.keywords || [],
          skills: s.skills || [],
          constraints: s.constraints || [],
          status: s.status || 'active'
        });
      }
    } catch (err) {
      setError('获取解决方案信息失败');
      console.error('获取解决方案失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // ========== 基础处理 ==========
  const handleChange = (e) => {
    const { name, value } = e.target;
    setSolution(prev => ({ ...prev, [name]: value }));
  };

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setSolution(prev => ({
      ...prev,
      type: newType,
      industry: INDUSTRIES[newType]?.[0] || prev.industry
    }));
  };

  // ========== 图片 ==========
  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const formData = new FormData();
    for (const file of files) formData.append('images', file);
    try {
      setUploadingImages(true);
      const token = localStorage.getItem('adminToken');
      const res = await axios.post('/api/admin/solutions/upload', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.urls) {
        setSolution(prev => ({ ...prev, images: [...prev.images, ...res.data.urls] }));
      }
    } catch (err) {
      console.error('上传图片失败:', err);
    } finally {
      setUploadingImages(false);
    }
  };

  const handleRemoveImage = (index) => {
    setSolution(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  // ========== 数组字段助手 ==========
  const addItem = (field, value, setter) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const arr = Array.isArray(solution[field]) ? solution[field] : [];
    if (!arr.includes(trimmed)) {
      setSolution(prev => ({ ...prev, [field]: [...arr, trimmed] }));
    }
    setter('');
  };

  const removeItem = (field, index) => {
    const arr = Array.isArray(solution[field]) ? solution[field] : [];
    setSolution(prev => ({ ...prev, [field]: arr.filter((_, i) => i !== index) }));
  };

  // 技能
  const addSkill = () => {
    if (!newSkillName.trim()) return;
    setSolution(prev => ({
      ...prev,
      skills: [...(prev.skills || []), { name: newSkillName.trim(), description: newSkillDesc.trim() }]
    }));
    setNewSkillName('');
    setNewSkillDesc('');
  };

  // 子方案
  const addSubSolution = () => {
    if (!newSubTitle.trim()) return;
    setSolution(prev => ({
      ...prev,
      subSolutions: [...(prev.subSolutions || []), {
        icon: newSubIcon.trim(),
        title: newSubTitle.trim(),
        description: newSubDesc.trim()
      }]
    }));
    setNewSubIcon('');
    setNewSubTitle('');
    setNewSubDesc('');
  };

  // ========== AI ==========
  const handleAIConvert = async () => {
    if (aiInput.trim().length < 20) return;
    setAiLoading(true);
    setAiMessage('');
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post('/api/admin/ai/convert-to-solution', { text: aiInput }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success && response.data.data) {
        const d = response.data.data;
        setSolution(prev => ({
          ...prev,
          title: d.title || prev.title,
          description: d.description || prev.description,
          type: d.type || prev.type,
          industry: d.industry || prev.industry,
          category: d.category || '',
          content: d.content || d.description || prev.content,
          features: d.features || [],
          benefits: d.benefits || [],
          applicationScenarios: d.applicationScenarios || [],
          technicalHighlights: d.technicalHighlights || [],
          subSolutions: d.subSolutions || [],
          tags: d.tags || prev.tags,
          keywords: d.keywords || prev.keywords
        }));
        setAiMessage('✅ AI填充成功！请检查并修改后提交。');
      }
    } catch (err) {
      setAiMessage('❌ AI转换失败: ' + (err.response?.data?.message || err.message));
    } finally {
      setAiLoading(false);
    }
  };

  // ========== 提交 ==========
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    const token = localStorage.getItem('adminToken');
    try {
      setLoading(true);
      if (isEditMode) {
        await axios.put(`/api/admin/solutions/${id}`, solution, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('解决方案更新成功！');
      } else {
        await axios.post('/api/admin/solutions', solution, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuccess('解决方案创建成功！');
      }
      setTimeout(() => navigate('/admin/solutions'), 1500);
    } catch (err) {
      setError(err.response?.data?.message || '保存解决方案失败');
    } finally {
      setLoading(false);
    }
  };

  const onEnter = (fn) => (e) => { if (e.key === 'Enter') { e.preventDefault(); fn(); } };

  if (loading && isEditMode && !solution.title) {
    return <AdminLayout title={isEditMode ? '编辑方案' : '添加方案'}><Container><SectionTitle>加载中...</SectionTitle></Container></AdminLayout>;
  }

  return (
    <AdminLayout title={isEditMode ? '编辑方案' : '添加方案'}>
      <Container>
        <SectionTitle>{isEditMode ? '编辑解决方案' : '添加解决方案'}</SectionTitle>

        {/* AI 智能填充 */}
        {!isEditMode && (
          <AISection>
            <AISectionHeader>
              <span>🤖 AI 智能填充</span>
              <AIToggleButton onClick={() => setShowAI(!showAI)}>{showAI ? '收起' : '展开'}</AIToggleButton>
            </AISectionHeader>
            {showAI && (
              <AIBody>
                <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.85)', marginBottom: '0.75rem' }}>
                  粘贴解决方案文档或需求描述，AI将自动识别并填充下方表单。
                </p>
                <AITextarea value={aiInput} onChange={e => setAiInput(e.target.value)} rows={6}
                  placeholder="请在此粘贴解决方案介绍文本（至少20个字符）..." />
                <AIButtonRow>
                  <AIConvertButton onClick={handleAIConvert} disabled={aiLoading || aiInput.trim().length < 20}>
                    {aiLoading ? '🔄 AI分析中...' : '✨ 智能填充表单'}
                  </AIConvertButton>
                  {aiMessage && <span style={{ fontSize: '0.8rem', marginLeft: '1rem' }}>{aiMessage}</span>}
                </AIButtonRow>
              </AIBody>
            )}
          </AISection>
        )}

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}

        <Form onSubmit={handleSubmit}>
          {/* ---- 基本信息 ---- */}
          <SubTitle>📋 基本信息</SubTitle>
          <FormGroup>
            <Label>解决方案标题 *</Label>
            <Input name="title" value={solution.title} onChange={handleChange} required placeholder="请输入解决方案标题" />
          </FormGroup>
          <FormRow>
            <FormGroup>
              <Label>解决方案类型 *</Label>
              <Select name="type" value={solution.type} onChange={handleTypeChange} required>
                {SOLUTION_TYPES.map(t => <option key={t} value={t}>{t}解决方案</option>)}
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>行业分类 *</Label>
              <Select name="industry" value={solution.industry} onChange={handleChange} required>
                {INDUSTRIES[solution.type]?.map(ind => <option key={ind} value={ind}>{ind}</option>)}
              </Select>
            </FormGroup>
          </FormRow>
          <FormRow>
            <FormGroup>
              <Label>二级分类（可选）</Label>
              <Input name="category" value={solution.category} onChange={handleChange} placeholder="如：呼叫中心、视频会议" />
            </FormGroup>
            <FormGroup>
              <Label>发布状态 *</Label>
              <Select name="status" value={solution.status} onChange={handleChange}>
                {STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </Select>
            </FormGroup>
          </FormRow>
          <FormRow>
            <FormGroup>
              <Label>图标（emoji 或 图标类名）</Label>
              <Input name="icon" value={solution.icon} onChange={handleChange} placeholder="如：🏥 或 fas fa-hospital" />
            </FormGroup>
          </FormRow>

          {/* ---- 描述与内容 ---- */}
          <SubTitle>📝 描述与内容</SubTitle>
          <FormGroup>
            <Label>解决方案简介（富文本）*</Label>
            <QuillWrapper>
              <ReactQuill
                value={solution.description}
                onChange={(content) => setSolution(prev => ({ ...prev, description: content }))}
                placeholder="用于列表展示的简短描述"
                modules={{
                  toolbar: [
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ color: [] }, { background: [] }],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['link', 'clean']
                  ]
                }}
                formats={['bold', 'italic', 'underline', 'strike', 'color', 'background', 'list', 'bullet', 'link']}
              />
            </QuillWrapper>
          </FormGroup>
          <FormGroup>
            <Label>解决方案详情（富文本）*</Label>
            <QuillWrapper>
              <ReactQuill
                value={solution.content}
                onChange={(content) => setSolution(prev => ({ ...prev, content: content }))}
                placeholder="完整的解决方案描述（支持富文本编辑：加粗、斜体、列表、图片、链接等）"
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline', 'strike'],
                    [{ color: [] }, { background: [] }],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    [{ align: [] }],
                    ['blockquote', 'code-block'],
                    ['link', 'image'],
                    ['clean']
                  ]
                }}
                formats={[
                  'header', 'bold', 'italic', 'underline', 'strike',
                  'color', 'background', 'list', 'bullet', 'align',
                  'blockquote', 'code-block', 'link', 'image'
                ]}
              />
            </QuillWrapper>
          </FormGroup>

          {/* ---- 特性与场景 ---- */}
          <SubTitle>✨ 特性与亮点</SubTitle>
          <FormRow>
            <FormGroup>
              <Label>解决方案特性</Label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <TagInput value={newFeature} onChange={e => setNewFeature(e.target.value)}
                  onKeyDown={onEnter(() => addItem('features', newFeature, setNewFeature))} placeholder="输入后按回车" />
                <SmallButton type="button" onClick={() => addItem('features', newFeature, setNewFeature)}>添加</SmallButton>
              </div>
              {(solution.features || []).length > 0 && (
                <TagContainer>
                  {solution.features.map((f, i) => (
                    <Tag key={i}>{f}<span onClick={() => removeItem('features', i)} style={{ marginLeft: '0.5rem', cursor: 'pointer' }}>×</span></Tag>
                  ))}
                </TagContainer>
              )}
            </FormGroup>
            <FormGroup>
              <Label>应用场景</Label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <TagInput value={newScenario} onChange={e => setNewScenario(e.target.value)}
                  onKeyDown={onEnter(() => addItem('applicationScenarios', newScenario, setNewScenario))} placeholder="输入后按回车" />
                <SmallButton type="button" onClick={() => addItem('applicationScenarios', newScenario, setNewScenario)}>添加</SmallButton>
              </div>
              {(solution.applicationScenarios || []).length > 0 && (
                <TagContainer>
                  {solution.applicationScenarios.map((s, i) => (
                    <Tag key={i} $bg="var(--success-color)">{s}
                      <span onClick={() => removeItem('applicationScenarios', i)} style={{ marginLeft: '0.5rem', cursor: 'pointer' }}>×</span>
                    </Tag>
                  ))}
                </TagContainer>
              )}
            </FormGroup>
          </FormRow>
          <FormGroup>
            <Label>方案优势（小程序「方案优势」展示此项，未填时回退技术亮点）</Label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <TagInput value={newBenefit} onChange={e => setNewBenefit(e.target.value)}
                onKeyDown={onEnter(() => addItem('benefits', newBenefit, setNewBenefit))} placeholder="输入后按回车" />
              <SmallButton type="button" onClick={() => addItem('benefits', newBenefit, setNewBenefit)}>添加</SmallButton>
            </div>
            {(solution.benefits || []).length > 0 && (
              <TagContainer>
                {solution.benefits.map((b, i) => (
                  <Tag key={i} $bg="#f59e0b">{b}
                    <span onClick={() => removeItem('benefits', i)} style={{ marginLeft: '0.5rem', cursor: 'pointer' }}>×</span>
                  </Tag>
                ))}
              </TagContainer>
            )}
          </FormGroup>
          <FormGroup>
            <Label>技术亮点</Label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <TagInput value={newTech} onChange={e => setNewTech(e.target.value)}
                onKeyDown={onEnter(() => addItem('technicalHighlights', newTech, setNewTech))} placeholder="输入后按回车" />
              <SmallButton type="button" onClick={() => addItem('technicalHighlights', newTech, setNewTech)}>添加</SmallButton>
            </div>
            {(solution.technicalHighlights || []).length > 0 && (
              <TagContainer>
                {solution.technicalHighlights.map((t, i) => (
                  <Tag key={i} $bg="#8b5cf6">{t}
                    <span onClick={() => removeItem('technicalHighlights', i)} style={{ marginLeft: '0.5rem', cursor: 'pointer' }}>×</span>
                  </Tag>
                ))}
              </TagContainer>
            )}
          </FormGroup>

          {/* ---- 子解决方案 ---- */}
          <SectionBlock>
            <h4 style={{ margin: '0 0 1rem', color: 'var(--gray-700)' }}>📦 子解决方案</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 2fr auto', gap: '0.5rem', alignItems: 'start', marginBottom: '0.75rem' }}>
              <Input value={newSubIcon} onChange={e => setNewSubIcon(e.target.value)} placeholder="图标" />
              <Input value={newSubTitle} onChange={e => setNewSubTitle(e.target.value)} placeholder="标题 *" />
              <Input value={newSubDesc} onChange={e => setNewSubDesc(e.target.value)} placeholder="描述" />
              <Button type="button" onClick={addSubSolution} style={{ padding: '0.75rem 1rem' }}>添加</Button>
            </div>
            {(solution.subSolutions || []).length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {solution.subSolutions.map((sub, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', background: 'white', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      {sub.icon && <span style={{ fontSize: '1.2rem' }}>{sub.icon}</span>}
                      <strong style={{ color: 'var(--primary-color)' }}>{sub.title}</strong>
                      {sub.description && <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>- {sub.description}</span>}
                    </div>
                    <span onClick={() => removeItem('subSolutions', i)} style={{ cursor: 'pointer', color: '#dc3545', fontWeight: 'bold' }}>×</span>
                  </div>
                ))}
              </div>
            )}
          </SectionBlock>

          {/* ---- 图片 ---- */}
          <SubTitle>🖼️ 方案图片</SubTitle>
          {(solution.images || []).length > 0 && (
            <ImageGrid>
              {solution.images.map((img, i) => (
                <ImageCard key={i}>
                  <ImagePreview src={img} alt={`solution-${i}`} onError={e => { e.target.style.display = 'none'; }} />
                  <ImageDeleteBtn type="button" onClick={() => handleRemoveImage(i)}>×</ImageDeleteBtn>
                </ImageCard>
              ))}
            </ImageGrid>
          )}
          <UploadArea style={{ marginTop: '0.75rem' }}>
            <input type="file" multiple accept="image/*" ref={fileInputRef} onChange={handleImageUpload} />
            <span style={{ fontSize: '1.5rem' }}>📁</span>
            <span>{uploadingImages ? '上传中...' : '点击上传图片（支持多选，单张≤5MB）'}</span>
          </UploadArea>

          {/* ---- 标签 ---- */}
          <FormGroup style={{ marginTop: '1.5rem' }}>
            <Label>解决方案标签</Label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <TagInput value={newTag} onChange={e => setNewTag(e.target.value)}
                onKeyDown={onEnter(() => addItem('tags', newTag, setNewTag))} placeholder="输入标签后按回车" />
              <SmallButton type="button" onClick={() => addItem('tags', newTag, setNewTag)}>添加</SmallButton>
            </div>
            {(solution.tags || []).length > 0 && (
              <TagContainer>
                {solution.tags.map((tag, i) => (
                  <Tag key={i}>{tag}
                    <span onClick={() => removeItem('tags', i)} style={{ marginLeft: '0.5rem', cursor: 'pointer' }}>×</span>
                  </Tag>
                ))}
              </TagContainer>
            )}
          </FormGroup>

          {/* ---- 智能体配置 ---- */}
          <SubTitle>🤖 智能体配置（可选）</SubTitle>

          <FormRow>
            <FormGroup>
              <Label>🔑 关键词配置</Label>
              <p style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '0.5rem' }}>用户提问包含以下关键词时，将优先推荐此方案</p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <TagInput value={newKeyword} onChange={e => setNewKeyword(e.target.value)}
                  onKeyDown={onEnter(() => addItem('keywords', newKeyword, setNewKeyword))} placeholder="输入关键词后按回车" />
                <SmallButton type="button" onClick={() => addItem('keywords', newKeyword, setNewKeyword)}>添加</SmallButton>
              </div>
              {(solution.keywords || []).length > 0 && (
                <TagContainer>
                  {solution.keywords.map((kw, i) => (
                    <Tag key={i} $bg="#17a2b8">{kw}
                      <span onClick={() => removeItem('keywords', i)} style={{ marginLeft: '0.5rem', cursor: 'pointer' }}>×</span>
                    </Tag>
                  ))}
                </TagContainer>
              )}
            </FormGroup>
            <FormGroup>
              <Label>⚠️ 限制条件</Label>
              <p style={{ fontSize: '0.8rem', color: '#6c757d', marginBottom: '0.5rem' }}>此方案的使用限制或约束条件</p>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <TagInput value={newConstraint} onChange={e => setNewConstraint(e.target.value)}
                  onKeyDown={onEnter(() => addItem('constraints', newConstraint, setNewConstraint))} placeholder="输入限制条件" />
                <SmallButton type="button" onClick={() => addItem('constraints', newConstraint, setNewConstraint)}>添加</SmallButton>
              </div>
              {(solution.constraints || []).length > 0 && (
                <TagContainer>
                  {solution.constraints.map((c, i) => (
                    <Tag key={i} $bg="#ffc107" $color="#212529">{c}
                      <span onClick={() => removeItem('constraints', i)} style={{ marginLeft: '0.5rem', cursor: 'pointer' }}>×</span>
                    </Tag>
                  ))}
                </TagContainer>
              )}
            </FormGroup>
          </FormRow>

          {/* 技能配置 */}
          <SectionBlock>
            <h4 style={{ margin: '0 0 1rem', color: 'var(--gray-700)' }}>🛠️ 技能配置</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: '0.5rem', alignItems: 'start', marginBottom: '0.75rem' }}>
              <Input value={newSkillName} onChange={e => setNewSkillName(e.target.value)} placeholder="技能名称" />
              <Input value={newSkillDesc} onChange={e => setNewSkillDesc(e.target.value)} placeholder="技能描述（可选）" />
              <Button type="button" onClick={addSkill} style={{ padding: '0.75rem 1rem' }}>添加</Button>
            </div>
            {(solution.skills || []).length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {solution.skills.map((skill, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', background: 'white', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                    <div>
                      <span style={{ fontWeight: 600, color: '#667eea' }}>{skill.name}</span>
                      {skill.description && <span style={{ color: '#6c757d', fontSize: '0.85rem', marginLeft: '0.5rem' }}>- {skill.description}</span>}
                    </div>
                    <span onClick={() => removeItem('skills', i)} style={{ cursor: 'pointer', color: '#dc3545', fontWeight: 'bold' }}>×</span>
                  </div>
                ))}
              </div>
            )}
          </SectionBlock>

          {/* ---- 按钮 ---- */}
          <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
            <CancelButton type="button" onClick={() => navigate('/admin/solutions')}>取消</CancelButton>
            <Button type="submit" disabled={loading}>
              {loading ? '保存中...' : (isEditMode ? '💾 更新解决方案' : '➕ 添加解决方案')}
            </Button>
          </div>
        </Form>
      </Container>
    </AdminLayout>
  );
};

export default AdminSolutionForm;
